/**
 * Wizard state machine — single source of truth for the build flow.
 *
 * Steps: character → exotic → set → stats → fragments → results
 * Each step's selections are stored in `state`; navigating backward never
 * clears later steps (so user can fork without losing work).
 *
 * Persisted to idb under `wizard:state` so a refresh restores the user
 * exactly where they were.
 */

import { get as idbGet, set as idbSet } from 'idb-keyval';

import type { ArmorClass, ArmorSlot } from '$domain/armor';

export const STEPS = [
  { id: 'character', label: 'Character', short: '01' },
  { id: 'exotic', label: 'Exotic', short: '02' },
  { id: 'set', label: 'Gear Set', short: '03' },
  { id: 'fragments', label: 'Fragments', short: '04' },
  { id: 'stats', label: 'Stats', short: '05' },
  { id: 'results', label: 'Results', short: '06' },
] as const;
export type StepId = (typeof STEPS)[number]['id'];

/** New D2 stat names (Edge of Fate onward). UI-facing only; the data layer
 *  still maps to whichever Bungie hashes are currently shipping armor stats. */
export const STAT_NAMES = ['health', 'melee', 'grenade', 'super', 'class', 'weapons'] as const;
export type StatName = (typeof STAT_NAMES)[number];
export type StatTuple = [number, number, number, number, number, number];

/**
 * Official Bungie stat icons. Same paths Bungie uses in-game / on
 * destinyitemmanager.com etc. Resolve through `bungieImg()` to get the full
 * CDN URL. Stored centrally so every step renders the same glyph instead of
 * hand-rolled unicode that drifts out of sync with the rename.
 */
export const STAT_ICONS: Record<StatName, string> = {
  health: '/common/destiny2_content/icons/717b8b218cc14325a54869bef21d2964.png',
  melee: '/common/destiny2_content/icons/fa534aca76d7f2d7e7b4ba4df4271b42.png',
  grenade: '/common/destiny2_content/icons/065cdaabef560e5808e821cefaeaa22c.png',
  super: '/common/destiny2_content/icons/585ae4ede9c3da96b34086fccccdc8cd.png',
  class: '/common/destiny2_content/icons/7eb845acb5b3a4a9b7e0b2f05f5c43f1.png',
  weapons: '/common/destiny2_content/icons/bc69675acdae9e6b9a68a02fb4d62e07.png',
};

/** Display labels for the stats — title-cased, used in tooltips and badges. */
export const STAT_LABELS: Record<StatName, string> = {
  health: 'Health',
  melee: 'Melee',
  grenade: 'Grenade',
  super: 'Super',
  class: 'Class',
  weapons: 'Weapons',
};

export interface WizardState {
  step: StepId;
  characterId: string | null;
  characterClass: ArmorClass | null;
  exoticHash: number | null; // null = any exotic
  exoticLocked: boolean;
  /** Gear-set constraints. Empty = no enforcement (any).
   *  Valid shapes:
   *    []                                            → any set
   *    [{ hash, count: 4 }]                          → 4pc of one set
   *    [{ hash, count: 2 }]                          → 2pc bonus only
   *    [{ hashA, count: 2 }, { hashB, count: 2 }]    → 2+2 combo
   *  Total pieces are always ≤ 4 (one slot is the exotic). */
  setSelections: SetSelection[];
  /** target stat values 0..200 per stat */
  targetStats: StatTuple;
  /** subclass hash chosen for fragment listing (set implicitly by super pick) */
  subclassHash: number | null;
  /** super (subclass plug) hash — the user's chosen super for this build.
   *  Picking a super pins the active element for the fragment grid. */
  superHash: number | null;
  /** fragment hashes selected */
  fragmentHashes: number[];
  /** Aspect hashes selected (max 2). Number of fragment slots in the build
   *  is the sum of each chosen aspect's fragmentSlots field. */
  aspectHashes: number[];
  /** Ability picks. Each is the plug hash of the chosen grenade / melee /
   *  class-ability / movement. `null` = leave the live socket alone. */
  abilityHashes: {
    grenade: number | null;
    melee: number | null;
    movement: number | null;
    classAbility: number | null;
  };
  /** When the locked exotic is an Exotic Class Item (Relativism / Stoicism /
   *  Solipsism), the two "Spirit of …" perks the user chose for it. The
   *  solver uses these to gate the requireExotic instance to one whose roll
   *  matches both. `null` for either column = no preference. */
  classItemPerks: { col1: number | null; col2: number | null };
  /** per-stat bonus (-30..+30) the user expects to apply via fragments+mods.
   *  the solver treats this as a free bonus added to each piece sum so floor
   *  checks against `targetStats` are met by raw+bonus combined. */
  fragmentStats: StatTuple;
  /** Solver knobs that don't fit the linear "target + bonus" model.
   *  Default state ships sensible-on flags so the first-time UX matches
   *  D2ArmorPicker; individual toggles can be flipped per-build via the
   *  Step 4 advanced disclosure. */
  solverOptions: SolverOptions;
  /** completed steps (so stepper can show checkmarks) */
  completed: StepId[];
}

export interface SolverOptions {
  /** Treat every non-masterworked legendary as if it were masterworked
   *  (+2 to every axis). Matches DAP's default. */
  assumeLegendaryMW: boolean;
  /** Same for exotics. */
  assumeExoticMW: boolean;
  /** Drop non-masterworked legendaries from the pool entirely. */
  onlyMWLegendaries: boolean;
  /** Drop non-masterworked exotics from the pool entirely. */
  onlyMWExotics: boolean;
  /** Assume tuning-mod allowance — each non-exotic piece can spend +5/-5
   *  on user-chosen axes. We apply it to the (axis the user weights
   *  highest / axis the user weights lowest); first-pass approximation
   *  of DAP's per-piece optimisation. */
  tuningMod: boolean;
  /** Per-slot artifice flag. Each true slot contributes +3 to the user's
   *  highest-weighted axis. */
  artifice: {
    helmet: boolean;
    gauntlets: boolean;
    chest: boolean;
    legs: boolean;
    classItem: boolean;
  };
  /** Post-filter: keep only loadouts where every axis is a multiple of 10
   *  (no wasted stats). */
  noWasted: boolean;
  /** Re-rank kept results by total wasted-stat count ascending (so the
   *  smallest waste floats up). Cheap pass over the top-K. */
  optimiseWasted: boolean;
  /** Strict upgrades mode: only show loadouts where EVERY stat is ≥ the
   *  reference loadout stats. Useful for finding strictly better builds
   *  without any trade-offs. */
  strictUpgradesOnly: boolean;
  /** Reference stats for strict upgrades mode. When strictUpgradesOnly is
   *  true, results are filtered to only those where every stat meets or
   *  exceeds these values. Typically set from current equipped loadout. */
  strictUpgradesReference: StatTuple | null;
}

export interface SetSelection {
  hash: number;
  count: 2 | 4;
}

const KEY = 'wizard:state:v1';

export function emptyState(): WizardState {
  return {
    step: 'character',
    characterId: null,
    characterClass: null,
    exoticHash: null,
    exoticLocked: false,
    setSelections: [],
    targetStats: [0, 0, 0, 0, 0, 0],
    subclassHash: null,
    superHash: null,
    fragmentHashes: [],
    aspectHashes: [],
    abilityHashes: {
      grenade: null,
      melee: null,
      movement: null,
      classAbility: null,
    },
    classItemPerks: { col1: null, col2: null },
    fragmentStats: [0, 0, 0, 0, 0, 0],
    solverOptions: defaultSolverOptions(),
    completed: [],
  };
}

/** Defaults that mirror D2ArmorPicker's initial settings: masterwork
 *  assumptions on, tuning on, artifice off (user must opt in per slot),
 *  wasted-stat filters off, strict upgrades off. */
export function defaultSolverOptions(): SolverOptions {
  return {
    assumeLegendaryMW: true,
    assumeExoticMW: true,
    onlyMWLegendaries: false,
    onlyMWExotics: false,
    tuningMod: true,
    artifice: {
      helmet: false,
      gauntlets: false,
      chest: false,
      legs: false,
      classItem: false,
    },
    noWasted: false,
    optimiseWasted: false,
    strictUpgradesOnly: false,
    strictUpgradesReference: null,
  };
}

/** Migrate an old shape (`setHash` + `setEnforce`) to the new `setSelections`
 *  array. No-op for already-migrated state. Idempotent. */
export function migrateState(
  raw: Partial<WizardState> & {
    setHash?: number | null;
    setEnforce?: 'off' | 'all';
  },
): Partial<WizardState> {
  if (Array.isArray(raw.setSelections)) return raw;
  const out: Partial<WizardState> & { setHash?: unknown; setEnforce?: unknown } = { ...raw };
  if (typeof raw.setHash === 'number' && raw.setEnforce === 'all') {
    out.setSelections = [{ hash: raw.setHash, count: 4 }];
  } else {
    out.setSelections = [];
  }
  delete out.setHash;
  delete out.setEnforce;
  return out;
}

export async function loadState(): Promise<WizardState> {
  const raw = await idbGet<WizardState>(KEY);
  if (!raw) return emptyState();
  // forward-compat: shape-merge so adding fields later doesn't break old saves
  const base = emptyState();
  const migrated = migrateState(raw);
  const merged: WizardState = { ...base, ...migrated };
  // solverOptions ships nested defaults; if the stored save predates the
  // field, shape-merge it too so toggles never read `undefined`.
  merged.solverOptions = {
    ...base.solverOptions,
    ...(migrated.solverOptions ?? {}),
    artifice: {
      ...base.solverOptions.artifice,
      ...(migrated.solverOptions?.artifice ?? {}),
    },
  };
  return merged;
}

export async function saveState(s: WizardState): Promise<void> {
  await idbSet(KEY, s);
}

export function stepIndex(id: StepId): number {
  return STEPS.findIndex((s) => s.id === id);
}

export function canAdvance(s: WizardState): boolean {
  switch (s.step) {
    case 'character':
      return s.characterId !== null;
    case 'exotic':
      return true; // null = "any exotic" is valid
    case 'set':
      return true;
    case 'stats':
      return true;
    case 'fragments':
      return true;
    case 'results':
      return false;
  }
}

export function nextStep(s: WizardState): StepId | null {
  const i = stepIndex(s.step);
  return i + 1 < STEPS.length ? STEPS[i + 1]!.id : null;
}

export function prevStep(s: WizardState): StepId | null {
  const i = stepIndex(s.step);
  return i > 0 ? STEPS[i - 1]!.id : null;
}

/** Bungie CDN image helper. icons are stored as relative paths like
 *  `/common/destiny2_content/icons/foo.png` — prefix with the bungie host. */
export function bungieImg(path: string | null | undefined): string {
  if (!path) return '';
  return path.startsWith('http') ? path : `https://www.bungie.net${path}`;
}

// ── share-link encoding ──────────────────────────────────────────────────
// Compact URL-friendly format for shareable builds. Skips characterId
// (it's account-specific) and only includes non-default fields so links
// are tiny.

interface SharePayload {
  c?: ArmorClass; // class
  e?: number; // exotic hash
  /** Compact set-selection encoding: array of [hash, count] tuples. */
  sx?: [number, 2 | 4][];
  /** Legacy single-set fields (read-only on decode for old links). */
  s?: number;
  se?: 'all';
  ts?: StatTuple; // target stats (omitted if all zeros)
  fs?: StatTuple; // fragment stats (omitted if all zeros)
  su?: number; // super hash
  fh?: number[]; // fragment hashes
  /** Exotic class item perk pair, compact: [col1Hash, col2Hash]. Omitted
   *  when both columns are unset. */
  cip?: [number | null, number | null];
}

function allZero(t: StatTuple): boolean {
  return t.every((v) => v === 0);
}

export function encodeShare(w: WizardState): string {
  const p: SharePayload = {};
  if (w.characterClass) p.c = w.characterClass;
  if (w.exoticHash !== null) p.e = w.exoticHash;
  if (w.setSelections.length > 0) {
    p.sx = w.setSelections.map((s) => [s.hash, s.count] as [number, 2 | 4]);
  }
  if (!allZero(w.targetStats)) p.ts = w.targetStats;
  if (!allZero(w.fragmentStats)) p.fs = w.fragmentStats;
  if (w.superHash !== null) p.su = w.superHash;
  if (w.fragmentHashes.length > 0) p.fh = [...w.fragmentHashes];
  if (w.classItemPerks.col1 !== null || w.classItemPerks.col2 !== null) {
    p.cip = [w.classItemPerks.col1, w.classItemPerks.col2];
  }
  // base64url of compact JSON; tiny payloads stay short
  const json = JSON.stringify(p);
  const b64 = typeof btoa === 'undefined' ? '' : btoa(json);
  return b64.replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
}

export function decodeShare(code: string): Partial<WizardState> | null {
  try {
    const pad = (s: string): string => s + '='.repeat((4 - (s.length % 4)) % 4);
    const b64 = pad(code.replaceAll('-', '+').replaceAll('_', '/'));
    const json = typeof atob === 'undefined' ? '' : atob(b64);
    const p = JSON.parse(json) as SharePayload;
    const patch: Partial<WizardState> = {};
    if (p.c) patch.characterClass = p.c;
    if (typeof p.e === 'number') patch.exoticHash = p.e;
    // New format wins. Fall back to legacy s/se for old shared links.
    if (Array.isArray(p.sx)) {
      patch.setSelections = p.sx
        .filter(
          (entry): entry is [number, 2 | 4] =>
            Array.isArray(entry) &&
            typeof entry[0] === 'number' &&
            (entry[1] === 2 || entry[1] === 4),
        )
        .map(([hash, count]) => ({ hash, count }));
    } else if (typeof p.s === 'number' && p.se === 'all') {
      patch.setSelections = [{ hash: p.s, count: 4 }];
    }
    if (Array.isArray(p.ts) && p.ts.length === 6) patch.targetStats = p.ts as StatTuple;
    if (Array.isArray(p.fs) && p.fs.length === 6) patch.fragmentStats = p.fs as StatTuple;
    if (typeof p.su === 'number') patch.superHash = p.su;
    if (Array.isArray(p.fh) && p.fh.every((x) => typeof x === 'number')) {
      patch.fragmentHashes = [...p.fh];
    }
    if (Array.isArray(p.cip) && p.cip.length === 2) {
      const col1 = typeof p.cip[0] === 'number' ? p.cip[0] : null;
      const col2 = typeof p.cip[1] === 'number' ? p.cip[1] : null;
      patch.classItemPerks = { col1, col2 };
    }
    return patch;
  } catch {
    return null;
  }
}

export type { ArmorClass, ArmorSlot };
